// "use client";
// import * as React from "react";
// import { Slot } from "@radix-ui/react-slot";
// import { cva, type VariantProps } from "class-variance-authority";
// import { Loader2 } from "lucide-react";

// import { cn } from "@/lib/utils";
// import {
//   TooltipProvider,
//   Tooltip,
//   TooltipContent,
//   TooltipTrigger,
// } from "./tooltip";

// const buttonVariants = cva(
//   "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] aria-pressed:bg-muted",
//   {
//     variants: {
//       variant: {
//         default: "bg-primary text-primary-foreground hover:bg-primary/90",
//         destructive:
//           "bg-destructive text-destructive-foreground hover:bg-destructive/90",
//         outline:
//           // MODIFIED: Use muted background and foreground border for elegant hover feedback.
//           "border border-input bg-background hover:bg-muted hover:border-foreground/30",
//         secondary:
//           "bg-secondary text-secondary-foreground hover:bg-secondary/80",
//         ghost:
//           // MODIFIED: Use only subtle muted background for hover. Text remains default foreground color.
//           "hover:bg-muted",
//         link: "text-primary underline-offset-4 hover:underline",
//       },
//       size: {
//         default: "h-10 px-4 py-2",
//         sm: "h-9 rounded-md px-3",
//         lg: "h-11 rounded-md px-8",
//         icon: "h-10 w-10",
//       },
//     },
//     defaultVariants: {
//       variant: "default",
//       size: "default",
//     },
//   }
// );
// type CommonProps = Omit<
//   React.ButtonHTMLAttributes<HTMLButtonElement> &
//   React.AnchorHTMLAttributes<HTMLAnchorElement>,
//   "href"
// >;

// interface ButtonBaseProps extends CommonProps {
//   href?: string;
// }

// export interface ButtonProps
//   extends ButtonBaseProps,
//   VariantProps<typeof buttonVariants> {
//   asChild?: boolean;
//   tooltipText?: string | React.ReactNode;

//   /**
//    * If omitted (undefined), the button is treated as "static" (no transient loader).
//    * If explicitly false, the button is interactive.
//    * If true, shows loading visuals.
//    */
//   isLoading?: boolean;

//   onClick?: (
//     e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
//   ) => void;

//   /**
//    * Previously used to silence interactive behavior and block clicks.
//    * Now isStatic only suppresses loading visuals by default.
//    */
//   isStatic?: boolean;

//   /**
//    * If true, the old behavior is preserved: isStatic will also block onClick.
//    * Default false.
//    */
//   blockOnStatic?: boolean;

//   disabled?: boolean;

//   /**
//    * When true, clicking the button will briefly show a loader even if `isLoading` isn't true.
//    */
//   showLoaderOnClick?: boolean;

//   /**
//    * When true, children are hidden while loading (useful for icon-only loader look).
//    */
//   hideChildrenWhenLoading?: boolean;

//   /**
//    * If provided, this text (or JSX) will be shown while loading alongside the loader.
//    */
//   loadingText?: React.ReactNode;
// }

// const Button = React.forwardRef<
//   HTMLButtonElement | HTMLAnchorElement,
//   ButtonProps
// >(
//   (
//     {
//       className,
//       variant,
//       size,
//       asChild = false,
//       tooltipText,
//       href,
//       isLoading, // note: may be undefined
//       isStatic = false,
//       blockOnStatic = false,
//       disabled = false,
//       children,
//       onClick,
//       showLoaderOnClick = true,
//       hideChildrenWhenLoading = false,
//       loadingText,
//       ...props
//     },
//     ref
//   ) => {
//     const Comp = asChild ? Slot : href ? "a" : "button";

//     // If caller omitted isLoading (undefined) we treat button as static by default.
//     // If caller passed isLoading={false}, they explicitly want interactive behavior.
//     const computedIsStatic = isStatic || isLoading === undefined;

//     // transient clicked state used to show loader when user clicks (for UX feedback)
//     const [clicked, setClicked] = React.useState(false);
//     const timeoutRef = React.useRef<number | null>(null);

//     // cleanup on unmount
//     React.useEffect(() => {
//       return () => {
//         if (timeoutRef.current) {
//           window.clearTimeout(timeoutRef.current);
//           timeoutRef.current = null;
//         }
//       };
//     }, []);

//     // If external isLoading becomes true, clear transient clicked state so prop drives UI.
//     React.useEffect(() => {
//       if (isLoading && clicked) {
//         setClicked(false);
//         if (timeoutRef.current) {
//           window.clearTimeout(timeoutRef.current);
//           timeoutRef.current = null;
//         }
//       }
//     }, [isLoading, clicked]);

//     // showLoading only when not computedIsStatic and either isLoading or clicked
//     const showLoading = !computedIsStatic && (Boolean(isLoading) || clicked);

//     // Disabled state should prevent action. Note: computedIsStatic does not imply disabled.
//     const isButtonDisabled = disabled || Boolean(isLoading) || (showLoaderOnClick && clicked);

//     const handleClick = (
//       e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
//     ) => {
//       e.stopPropagation();
//       // e.preventDefault();
//       // Block action if actually disabled or loading; do NOT block just because computedIsStatic
//       // unless the caller explicitly asked for that via blockOnStatic.
//       if (disabled || isLoading || (blockOnStatic && computedIsStatic)) {
//         e.preventDefault();
//         return;
//       }

//       // Call consumer onClick
//       if (onClick) onClick(e);

//       // If configured, show a transient loader on click
//       if (showLoaderOnClick && !isLoading && !computedIsStatic) {
//         setClicked(true);
//         if (timeoutRef.current) {
//           window.clearTimeout(timeoutRef.current);
//         }
//         timeoutRef.current = window.setTimeout(() => {
//           setClicked(false);
//           timeoutRef.current = null;
//         }, 3000);
//       }
//     };

//     const baseClass = cn(buttonVariants({ variant, size, className }));

//     // For anchors, `disabled` prop is invalid — instead use aria-disabled and prevent clicks/tabbing.
//     const anchorA11yProps =
//       Comp === "a" && isButtonDisabled
//         ? {
//           "aria-disabled": true,
//           tabIndex: -1,
//           className: cn(baseClass, "pointer-events-none opacity-50"),
//           onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
//             e.preventDefault();
//           },
//         }
//         : {};

//     const renderLoadingContent = () => {
//       if (loadingText !== undefined) {
//         return (
//           <>
//             <Loader2 className="mr-2 h-4 w-4 animate-spin" />
//             {loadingText}
//           </>
//         );
//       }

//       return (
//         <>
//           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
//           {!hideChildrenWhenLoading && children}
//         </>
//       );
//     };

//     const content = (
//       <Comp
//         ref={ref as any}
//         {...(Comp === "button" ? { disabled: isButtonDisabled } : {})}
//         {...(href ? { href } : {})}
//         {...props}
//         {...anchorA11yProps}
//         className={Comp === "a" && isButtonDisabled ? cn(anchorA11yProps.className) : baseClass}
//         onClick={handleClick}
//         aria-busy={showLoading ? true : undefined}
//       >
//         {showLoading ? renderLoadingContent() : children}
//       </Comp>
//     );

//     return tooltipText ? (
//       <TooltipProvider>
//         <Tooltip>
//           <TooltipTrigger asChild>
//             <span
//               className={cn(disabled && "cursor-not-allowed", "inline-block")}
//             >
//               {content}
//             </span>
//           </TooltipTrigger>
//           <TooltipContent
//             className="bg-black text-white p-2 rounded shadow-lg border border-card-foreground/30"
//             side="top"
//             align="center"
//             sideOffset={5}
//           >
//             {tooltipText}
//           </TooltipContent>
//         </Tooltip>
//       </TooltipProvider>
//     ) : (
//       content
//     );
//   }
// );

// Button.displayName = "Button";

// export { Button, buttonVariants };

"use client";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./tooltip";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] aria-pressed:bg-muted",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-muted hover:border-foreground/30",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

type CommonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
>;

interface ButtonBaseProps extends CommonProps {
  href?: string;
}

export interface ButtonProps
  extends ButtonBaseProps,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltipText?: string | React.ReactNode;
  isLoading?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  isStatic?: boolean;
  blockOnStatic?: boolean;
  disabled?: boolean;
  showLoaderOnClick?: boolean;
  hideChildrenWhenLoading?: boolean;
  loadingText?: React.ReactNode;
}

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      tooltipText,
      href,
      isLoading,
      isStatic = false,
      blockOnStatic = false,
      disabled = false,
      children,
      onClick,
      showLoaderOnClick = true,
      hideChildrenWhenLoading = false,
      loadingText,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : href ? "a" : "button";
    // const computedIsStatic = isStatic || isLoading === undefined;
    const computedIsStatic = isStatic || (isLoading === undefined && !href);
    const [clicked, setClicked] = React.useState(false);
    const timeoutRef = React.useRef<number | null>(null);

    React.useEffect(() => {
      return () => {
        if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      };
    }, []);

    React.useEffect(() => {
      if (isLoading && clicked) {
        setClicked(false);
        if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      }
    }, [isLoading, clicked]);

    const showLoading = !computedIsStatic && (Boolean(isLoading) || clicked);
    
    // Logic fix: Only disable if it's a real action button, not just because it's "static"
    const isButtonDisabled = disabled || Boolean(isLoading) || (showLoaderOnClick && clicked);

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
      // 1. Check if we should block the click
      if (disabled || isLoading || (blockOnStatic && computedIsStatic)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // 2. Call the custom onClick handler if provided
      if (onClick) onClick(e);

      // 3. Handle transient loading state
      if (showLoaderOnClick && !isLoading && !computedIsStatic) {
        setClicked(true);
        if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
          setClicked(false);
        }, 3000);
      }
      
      // Note: We do NOT call e.preventDefault() here, 
      // allowing the browser to follow the href if it's an <a> tag.
    };

    const baseClass = cn(buttonVariants({ variant, size, className }));

    const anchorA11yProps =
      Comp === "a" && isButtonDisabled
        ? {
          "aria-disabled": true,
          tabIndex: -1,
          className: cn(baseClass, "pointer-events-none opacity-50"),
          onClick: (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault(),
        }
        : {};

    const renderLoadingContent = () => (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        {loadingText !== undefined ? loadingText : (!hideChildrenWhenLoading && children)}
      </>
    );

    const content = (
      <Comp
        ref={ref as any}
        {...(Comp === "button" ? { disabled: isButtonDisabled } : {})}
        {...(href ? { href } : {})}
        {...props}
        {...anchorA11yProps}
        className={Comp === "a" && isButtonDisabled ? cn(anchorA11yProps.className) : baseClass}
        onClick={handleClick}
        aria-busy={showLoading ? true : undefined}
      >
        {showLoading ? renderLoadingContent() : children}
      </Comp>
    );

    return tooltipText ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(disabled && "cursor-not-allowed", "inline-block")}>
              {content}
            </span>
          </TooltipTrigger>
          <TooltipContent
            className="bg-black text-white p-2 rounded shadow-lg border border-card-foreground/30"
            side="top"
            align="center"
            sideOffset={5}
          >
            {tooltipText}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      content
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };